import {
  ChangeDetectorRef,
  Directive,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { ConfigurationsService } from '../services/configurations/configurations.service';

@Directive({
  selector: '[epcFeatureFlag]',
  exportAs: 'epcFeatureFlag',
  standalone: false,
})
export class FeatureFlagDirective<T> implements OnChanges, OnDestroy {
  @Input()
  public epcFeatureFlag: string;

  @Input()
  public epcFeatureFlagNamespace: string;

  /***
   * If epcFeatureFlagEquality is a typeof
   *  - boolean/string/number: true/false;'valueToCheck';1
   *  - fn: then make equalityWrapperFn and pass it here to save your args in a closure
   * e.g.
   * `const equalityFn = (myVariableIcompareWith) => (resultValueFromRuleEngine) => resultValueFromRuleEngine === myVariableIcompareWith`
   * and pass it to the component like `[epcFeatureFlagEquality]="equalityFn(myVariableIcompareWith)"`
   */
  @Input()
  public epcFeatureFlagEquality: any; // tslint:disable-line:no-any

  @Input()
  public epcFeatureFlagDefaultValue: any = false; // tslint:disable-line:no-any

  private destroy$$ = new Subject<void>();

  private featureCheck$$ = new Subject<void>();

  constructor(
    private templateRef: TemplateRef<T>,
    private vcRef: ViewContainerRef,
    private configurationsService: ConfigurationsService,
    private cdr: ChangeDetectorRef,
  ) {
    this.featureCheck$$
      .pipe(
        switchMap(() =>
          this.configurationsService.getConfigValue({
            namespace: this.epcFeatureFlagNamespace,
            featureName: this.epcFeatureFlag,
            defaultValue: this.epcFeatureFlagDefaultValue,
          }),
        ),
        map((value) => {
          // To check null AND undefined
          if (this.epcFeatureFlagEquality == null) {
            return this.epcFeatureFlagDefaultValue;
          }

          switch (typeof this.epcFeatureFlagEquality) {
            case 'function':
              return this.epcFeatureFlagEquality(value);
            case 'boolean':
            case 'string':
            case 'number':
            default:
              return this.epcFeatureFlagEquality === value;
          }
        }),
        takeUntil(this.destroy$$),
      )
      .subscribe((passed) => {
        if (!passed) {
          this.vcRef.clear();
        } else {
          this.vcRef.createEmbeddedView(this.templateRef);
        }
        this.cdr.markForCheck();
      });
  }

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    const epcFeatureFlag = simpleChanges.epcFeatureFlag?.currentValue;
    const namespace = simpleChanges.epcFeatureFlagNamespace?.currentValue;
    if (!epcFeatureFlag || !namespace) {
      return;
    }
    this.featureCheck$$.next();
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }
}
